import { ChevronDown, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import {
  bookCalendlyDemo,
  fetchUpcomingAvailability,
  formatCalendlyLoadError,
  formatSlotTime,
  getUserTimezone,
  groupSlotsByLocalDay,
  hasFirstAndLastName,
  type AvailabilitySlot,
  type CalendlyAudience,
  type CalendlyBookingResult,
} from '@/lib/calendlyBooking'
import { trackDemoBooked } from '@/lib/openaiAds'

type SchedulerTheme = {
  input: string
  inputError: string
  chipDefault: string
  chipActive: string
  sectionLabel: string
  splitBorder: string
  bookButton: string
  mutedText: string
  errorText: string
  link: string
  spinner: string
  readOnlyInput: string
}

const THEMES: Record<'dark' | 'light', SchedulerTheme> = {
  dark: {
    input:
      'w-full min-h-[48px] rounded-2xl border border-white/12 bg-[#1a1820] px-4 py-3 text-base text-white placeholder:text-[#8b8494] outline-none focus:border-[#71cdd9] focus:ring-2 focus:ring-[#71cdd9]/20 transition-colors',
    inputError:
      'w-full min-h-[48px] rounded-2xl border-2 border-red-400 bg-red-950/20 px-4 py-3 text-base text-white placeholder:text-[#8b8494] outline-none ring-2 ring-red-400/30 shadow-[0_0_0_4px_rgba(248,113,113,0.12)] motion-safe:animate-pulse focus:border-red-400 focus:ring-red-400/40 transition-colors',
    chipDefault:
      'cursor-pointer rounded-xl border border-white/12 bg-[#25222c] text-[#e5e7eb] hover:border-[#71cdd9]/50 hover:bg-[#2f2b36] transition-colors min-h-[44px]',
    chipActive:
      'cursor-pointer rounded-xl border border-[#71cdd9] bg-[#71cdd9] text-[#0a0f14] shadow-md shadow-[#71cdd9]/20 font-semibold min-h-[44px]',
    sectionLabel:
      'text-[13px] font-medium text-white/70 mb-3',
    splitBorder: 'lg:border-l lg:border-white/10 lg:pl-12',
    bookButton:
      'cursor-pointer w-full min-h-[52px] rounded-2xl py-4 text-[15px] font-semibold text-[#0a0f14] bg-[#71cdd9] hover:bg-[#9de4ed] shadow-lg shadow-[#71cdd9]/25 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none transition-colors',
    mutedText: 'text-[#9ca3af]',
    errorText: 'text-red-400',
    link: 'text-[#71cdd9] hover:underline',
    spinner: 'text-[#71cdd9]',
    readOnlyInput:
      'w-full rounded-2xl border border-white/12 bg-[#1a1820] px-4 py-3 text-[15px] text-[#e5e7eb] cursor-default',
  },
  light: {
    input:
      'w-full rounded-xl border border-[var(--signup-border-default)] bg-white px-4 py-3 text-[15px] text-[var(--signup-text-primary)] placeholder:text-[var(--signup-text-tertiary)] outline-none focus:border-[var(--signup-accent)] focus:ring-2 focus:ring-[var(--signup-accent)]/20 transition-colors',
    inputError:
      'w-full rounded-xl border-2 border-red-500 bg-red-50 px-4 py-3 text-[15px] text-[var(--signup-text-primary)] placeholder:text-[var(--signup-text-tertiary)] outline-none ring-2 ring-red-500/25 shadow-[0_0_0_4px_rgba(239,68,68,0.1)] motion-safe:animate-pulse focus:border-red-500 focus:ring-red-500/35 transition-colors',
    chipDefault:
      'cursor-pointer rounded-xl border border-[var(--signup-border-default)] bg-[var(--signup-bg-elevated)] text-[var(--signup-text-primary)] hover:border-[var(--signup-accent)]/50 hover:bg-[var(--signup-bg-subtle)] transition-colors min-h-[44px]',
    chipActive:
      'cursor-pointer rounded-xl border border-[var(--signup-accent)] bg-[var(--signup-accent-subtle)] text-[var(--signup-accent)] font-semibold min-h-[44px]',
    sectionLabel:
      'text-[13px] font-medium text-[var(--signup-text-secondary)] mb-3',
    splitBorder: 'lg:border-l lg:border-[var(--signup-border-subtle)] lg:pl-8',
    bookButton:
      'cursor-pointer w-full min-h-[48px] rounded-2xl py-3.5 text-[15px] font-medium text-white bg-[var(--signup-accent)] hover:bg-[var(--signup-accent-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors motion-safe:duration-150',
    mutedText: 'text-[var(--signup-text-secondary)]',
    errorText: 'text-red-600',
    link: 'text-[var(--signup-accent)] hover:underline',
    spinner: 'text-[var(--signup-accent)]',
    readOnlyInput:
      'w-full rounded-xl border border-[var(--signup-border-subtle)] bg-[var(--signup-bg-subtle)] px-4 py-3 text-[15px] text-[var(--signup-text-primary)] cursor-default',
  },
}

type BookDemoSchedulerProps = {
  audience?: CalendlyAudience
  /** dark = /book-demo; light = signup / setup mode shells */
  variant?: 'dark' | 'light'
  /** compact = date/time + confirm; stacked = all fields in one column (sidebar) */
  layout?: 'full' | 'compact' | 'stacked'
  name: string
  email: string
  onNameChange: (value: string) => void
  onEmailChange: (value: string) => void
  onBooked: (
    booking: CalendlyBookingResult,
    lead?: {
      name: string
      email: string
      organization?: string
      phone?: string
      message?: string
      teamSize?: string
    },
  ) => void
  defaultOrganization?: string
  defaultMessage?: string
  confirmLabel?: string
  organizationPlaceholder?: string
  messagePlaceholder?: string
  /** Friendly line under the confirm button. */
  footerNote?: string
  /** When set, replaces the notes textarea with size pills (message sent to Calendly). */
  teamSizeOptions?: { id: string; label: string }[]
  teamSize?: string
  onTeamSizeChange?: (id: string) => void
  teamSizeSectionLabel?: string
  /** Name/email from SSO — show read-only on signup booking pages. */
  lockContactFields?: boolean
  /** Pin the confirm CTA to the bottom of the viewport on small screens. */
  stickyMobileConfirm?: boolean
}

function buildAcademyBookingDetails(
  academyName: string,
  teamSize: string,
  teamSizeOptions: { id: string; label: string }[],
) {
  const parts = []
  const org = academyName.trim()
  if (org) parts.push(`Academy: ${org}`)
  if (teamSize) {
    const label =
      teamSizeOptions.find((o) => o.id === teamSize)?.label || teamSize
    parts.push(`Rough team size: ${label}`)
  }
  return parts.length > 0 ? parts.join('\n') : undefined
}

function buildCommunityBookingDetails(
  communityName: string,
  communitySize: string,
  sizeOptions: { id: string; label: string }[],
) {
  const parts = []
  const org = communityName.trim()
  if (org) parts.push(`Community: ${org}`)
  if (communitySize) {
    const label =
      sizeOptions.find((o) => o.id === communitySize)?.label || communitySize
    parts.push(`Rough player count: ${label}`)
  }
  return parts.length > 0 ? parts.join('\n') : undefined
}

function buildVenueBookingDetails(
  venueName: string,
  courtRange: string,
  courtRangeOptions: { id: string; label: string }[],
) {
  const parts = []
  const org = venueName.trim()
  if (org) parts.push(`Venue: ${org}`)
  if (courtRange) {
    const label =
      courtRangeOptions.find((o) => o.id === courtRange)?.label || courtRange
    parts.push(`Rough court count: ${label}`)
  }
  return parts.length > 0 ? parts.join('\n') : undefined
}

function SectionLabel({
  theme,
  children,
}: {
  theme: SchedulerTheme
  children: React.ReactNode
}) {
  return <p className={theme.sectionLabel}>{children}</p>
}

function ContactNameEmailFields({
  theme,
  name,
  email,
  lockContactFields,
  submitAttempted,
  onNameChange,
  onEmailChange,
}: {
  theme: SchedulerTheme
  name: string
  email: string
  lockContactFields: boolean
  submitAttempted: boolean
  onNameChange: (value: string) => void
  onEmailChange: (value: string) => void
}) {
  const [nameTouched, setNameTouched] = useState(false)
  const trimmedName = name.trim()
  const nameNeedsLastName =
    trimmedName.length > 0 && !hasFirstAndLastName(name)
  const showNameError = nameNeedsLastName && (nameTouched || submitAttempted)
  const nameHintId = 'book-demo-name-hint'

  if (lockContactFields) {
    const nameIncomplete = !hasFirstAndLastName(name)
    if (!nameIncomplete) {
      return (
        <div className="space-y-3">
          <div className={theme.readOnlyInput} aria-readonly="true">
            {trimmedName || '—'}
          </div>
          <div className={theme.readOnlyInput} aria-readonly="true">
            {email.trim() || '—'}
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-3">
        <div>
          <input
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            onBlur={() => setNameTouched(true)}
            aria-invalid={showNameError}
            aria-describedby={showNameError ? nameHintId : undefined}
            className={showNameError ? theme.inputError : theme.input}
            placeholder="Your name"
          />
          {showNameError ? (
            <p
              id={nameHintId}
              role="alert"
              className={`mt-2 text-[13px] leading-snug ${theme.errorText} motion-safe:animate-pulse`}
            >
              Add your last name too — e.g.{' '}
              <span className="font-medium">{trimmedName} Smith</span>
            </p>
          ) : (
            <p className={`mt-2 text-[12px] leading-snug ${theme.mutedText}`}>
              We only got a first name from sign-in — add your last name so the invite looks right.
            </p>
          )}
        </div>
        <div className={theme.readOnlyInput} aria-readonly="true">
          {email.trim() || '—'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div>
        <input
          type="text"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={() => setNameTouched(true)}
          aria-invalid={showNameError}
          aria-describedby={showNameError ? nameHintId : undefined}
          className={showNameError ? theme.inputError : theme.input}
          placeholder="Your name"
        />
        {showNameError ? (
          <p
            id={nameHintId}
            role="alert"
            className={`mt-2 text-[13px] leading-snug ${theme.errorText} motion-safe:animate-pulse`}
          >
            Add your last name too — e.g.{' '}
            <span className="font-medium">{trimmedName} Smith</span>
          </p>
        ) : (
          <p className={`mt-2 text-[12px] leading-snug ${theme.mutedText}`}>
            First and last name — we'll put this on the invite.
          </p>
        )}
      </div>
      <input
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        className={theme.input}
        placeholder="Your email"
      />
    </div>
  )
}

export function BookDemoScheduler({
  audience = 'community',
  variant = 'dark',
  layout = 'full',
  name,
  email,
  onNameChange,
  onEmailChange,
  onBooked,
  defaultOrganization = '',
  defaultMessage = '',
  confirmLabel = 'Book this time',
  organizationPlaceholder = 'Club, academy, or venue',
  messagePlaceholder =
    "Anything we should know? Sport, how many games, what you're hoping to see…",
  footerNote,
  teamSizeOptions,
  teamSize = '',
  onTeamSizeChange,
  teamSizeSectionLabel = 'Roughly how big?',
  lockContactFields = false,
  stickyMobileConfirm = false,
}: BookDemoSchedulerProps) {
  const useTeamSize = !!teamSizeOptions?.length && !!onTeamSizeChange
  const theme = THEMES[variant]
  const timezone = getUserTimezone()
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [slots, setSlots] = useState<AvailabilitySlot[]>([])
  const [selectedDay, setSelectedDay] = useState('')
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null)
  const [organization, setOrganization] = useState(defaultOrganization)
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState(defaultMessage)
  const [booking, setBooking] = useState(false)
  const [bookError, setBookError] = useState('')
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const isCompact = layout === 'compact'
  const isStacked = layout === 'stacked'
  const contactIncomplete =
    name.trim().length < 2 ||
    !email.trim().includes('@') ||
    !hasFirstAndLastName(name)
  const showContactFields = isStacked || !isCompact || contactIncomplete

  useEffect(() => {
    if (defaultOrganization && !organization.trim()) {
      setOrganization(defaultOrganization)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOrganization])

  useEffect(() => {
    setMessage(defaultMessage)
  }, [defaultMessage])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError('')

    fetchUpcomingAvailability(7, audience)
      .then((available) => {
        if (cancelled) return
        setSlots(available)
        const groups = groupSlotsByLocalDay(available, timezone)
        setSelectedDay(groups[0]?.dayKey || '')
      })
      .catch((err) => {
        if (cancelled) return
        const message =
          err instanceof Error
            ? err.message
            : "We couldn't load times just now. Give it another try."
        setLoadError(formatCalendlyLoadError(message))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [audience])

  const dayGroups = useMemo(
    () => groupSlotsByLocalDay(slots, timezone),
    [slots, timezone],
  )

  const daySlots =
    dayGroups.find((g) => g.dayKey === selectedDay)?.slots || []

  const organizationValue = organization.trim()
  const hasValidContact =
    name.trim().length > 1 &&
    email.trim().includes('@') &&
    hasFirstAndLastName(name)

  const canBook = !!selectedSlot && hasValidContact && !booking

  const readyHint = useMemo(() => {
    if (canBook || booking) return ''
    if (!selectedSlot) return "Pick a day and time — then we'll take it from there."
    if (!hasValidContact) {
      if (!hasFirstAndLastName(name)) {
        return 'Add your first and last name so the invite looks right.'
      }
      if (!email.trim().includes('@')) return 'Drop in an email so we can send the invite.'
    }
    return ''
  }, [canBook, booking, selectedSlot, hasValidContact, name, email])

  const missingFieldsHint = useMemo(() => {
    if (!submitAttempted || canBook) return ''
    const missing = []
    if (!selectedSlot) missing.push('a time')
    if (name.trim().length < 2 || !hasFirstAndLastName(name)) {
      missing.push('your first and last name')
    }
    if (!email.trim().includes('@')) missing.push('your email')
    if (missing.length === 0) return ''
    const needed =
      missing.length === 1
        ? missing[0]
        : missing.length === 2
          ? `${missing[0]} and ${missing[1]}`
          : `${missing.slice(0, -1).join(', ')}, and ${missing[missing.length - 1]}`
    return `Just need ${needed} — then you're in.`
  }, [submitAttempted, canBook, selectedSlot, name, email])

  const bookingMessage = useMemo(() => {
    if (useTeamSize && teamSizeOptions) {
      if (audience === 'venue') {
        return buildVenueBookingDetails(
          organizationValue,
          teamSize,
          teamSizeOptions,
        )
      }
      if (audience === 'community') {
        return buildCommunityBookingDetails(
          organizationValue,
          teamSize,
          teamSizeOptions,
        )
      }
      return buildAcademyBookingDetails(
        organizationValue,
        teamSize,
        teamSizeOptions,
      )
    }
    return (message.trim() || defaultMessage.trim()) || undefined
  }, [
    audience,
    useTeamSize,
    teamSize,
    teamSizeOptions,
    organizationValue,
    message,
    defaultMessage,
  ])

  const handleConfirm = async () => {
    setSubmitAttempted(true)
    if (!canBook) {
      if (missingFieldsHint) setBookError(missingFieldsHint)
      return
    }
    setBookError('')
    setBooking(true)
    try {
      const result = await bookCalendlyDemo(
        {
          name: name.trim(),
          email: email.trim(),
          audience,
          organization: organizationValue || undefined,
          phone: phone.trim() || undefined,
          message: bookingMessage,
        },
        selectedSlot.start_time,
      )
      trackDemoBooked()
      const org = organizationValue || undefined
      onBooked(result, {
        name: name.trim(),
        email: email.trim(),
        organization: org,
        phone: phone.trim() || undefined,
        message: bookingMessage,
        teamSize: useTeamSize ? teamSize || undefined : undefined,
      })
    } catch (err) {
      setBookError(
        err instanceof Error
          ? err.message
          : "That slot just got taken. Want to try another time?",
      )
    } finally {
      setBooking(false)
    }
  }

  const shellPad = isCompact || isStacked ? '' : 'p-6 sm:p-8 lg:p-10'

  if (loading) {
    return (
      <div
        className={`${isCompact ? 'py-16' : 'py-24'} flex flex-col items-center gap-3 ${theme.mutedText}`}
      >
        <Loader2
          className={`animate-spin ${theme.spinner}`}
          size={28}
          aria-hidden
        />
        <p className="text-sm">Finding times that work…</p>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={isCompact ? 'py-6' : 'p-8 sm:p-10'}>
        <p role="alert" className={`text-sm ${theme.errorText} text-center`}>
          {loadError}
        </p>
      </div>
    )
  }

  if (slots.length === 0) {
    return (
      <div className={isCompact ? 'py-6 text-center' : 'p-8 sm:p-10 text-center'}>
        <p className={`text-sm ${theme.mutedText}`}>
          Looks like this week&apos;s full. Email us at{' '}
          <a href="mailto:support@statbox.co" className={theme.link}>
            support@statbox.co
          </a>{' '}
          and we&apos;ll find a time.
        </p>
      </div>
    )
  }

  const renderConfirmButton = () => (
    <div>
      <button
        type="button"
        disabled={booking}
        onClick={handleConfirm}
        className={theme.bookButton}
      >
        {booking ? (
          <span className="inline-flex items-center justify-center gap-2">
            <Loader2 size={17} className="animate-spin" aria-hidden />
            Locking it in…
          </span>
        ) : (
          confirmLabel
        )}
      </button>
      {footerNote && !booking ? (
        <p className={`mt-2.5 text-center text-[12px] leading-snug ${theme.mutedText}`}>
          {footerNote}
        </p>
      ) : null}
    </div>
  )

  const renderConfirmHints = () => (
    <>
      {(bookError || (submitAttempted && missingFieldsHint)) && (
        <p role="alert" className={`text-sm ${theme.errorText}`}>
          {bookError || missingFieldsHint}
        </p>
      )}
      {canBook && !booking && !bookError && (
        <p className={`text-[13px] leading-snug ${theme.mutedText}`}>
          Looks good — we&apos;ll send the invite as soon as you confirm.
        </p>
      )}
      {!canBook && !booking && readyHint && !submitAttempted && (
        <p className={`text-[13px] leading-snug ${theme.mutedText}`}>
          {readyHint}
        </p>
      )}
    </>
  )

  const schedulingBlock = (
    <div className={isCompact ? 'space-y-6' : 'space-y-7'}>
      <div>
        <SectionLabel theme={theme}>Which day works?</SectionLabel>
        <div className="relative md:hidden">
          <select
            aria-label="Which day works?"
            value={selectedDay}
            onChange={(e) => {
              setSelectedDay(e.target.value)
              setSelectedSlot(null)
            }}
            className={`${theme.input} appearance-none pr-12 cursor-pointer`}
            style={variant === 'dark' ? { colorScheme: 'dark' } : undefined}
          >
            {dayGroups.map((group) => (
              <option key={group.dayKey} value={group.dayKey}>
                {group.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={18}
            aria-hidden
            className={`pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 ${theme.mutedText}`}
          />
        </div>
        <div className="hidden md:flex flex-wrap gap-2">
            {dayGroups.map((group) => {
              const active = group.dayKey === selectedDay
              return (
                <button
                  key={group.dayKey}
                  type="button"
                  onClick={() => {
                    setSelectedDay(group.dayKey)
                    setSelectedSlot(null)
                  }}
                  className={`shrink-0 px-4 py-2.5 text-sm font-medium ${
                    active ? theme.chipActive : theme.chipDefault
                  }`}
                >
                  {group.label}
                </button>
              )
            })}
        </div>
      </div>

      <div>
        <SectionLabel theme={theme}>What time?</SectionLabel>
        <div className="relative md:hidden">
          <select
            aria-label="What time works?"
            value={selectedSlot?.start_time ?? ''}
            onChange={(e) => {
              const next = daySlots.find((slot) => slot.start_time === e.target.value) || null
              setSelectedSlot(next)
            }}
            className={`${theme.input} appearance-none pr-12 cursor-pointer`}
            style={variant === 'dark' ? { colorScheme: 'dark' } : undefined}
          >
            <option value="" disabled>
              {daySlots.length === 0 ? 'No times left this day' : 'A time that works for you'}
            </option>
            {daySlots.map((slot) => (
              <option key={slot.start_time} value={slot.start_time}>
                {formatSlotTime(slot.start_time, timezone)}
              </option>
            ))}
          </select>
          <ChevronDown
            size={18}
            aria-hidden
            className={`pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 ${theme.mutedText}`}
          />
        </div>
        <div className="hidden md:grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {daySlots.map((slot) => {
            const active = selectedSlot?.start_time === slot.start_time
            return (
              <button
                key={slot.start_time}
                type="button"
                onClick={() => setSelectedSlot(slot)}
                className={`py-3 text-sm ${
                  active ? theme.chipActive : theme.chipDefault
                }`}
              >
                {formatSlotTime(slot.start_time, timezone)}
              </button>
            )
          })}
        </div>
      </div>

      {isCompact && (
        <>
          {showContactFields && (
            <div className="space-y-3">
              <SectionLabel theme={theme}>A bit about you</SectionLabel>
              <ContactNameEmailFields
                theme={theme}
                name={name}
                email={email}
                lockContactFields={lockContactFields}
                submitAttempted={submitAttempted}
                onNameChange={onNameChange}
                onEmailChange={onEmailChange}
              />
            </div>
          )}

          {renderConfirmHints()}

          <div className={stickyMobileConfirm ? 'hidden lg:block' : undefined}>
            {renderConfirmButton()}
          </div>
        </>
      )}
    </div>
  )

  const detailsColumn = (
        <div
          className={
            isStacked
              ? 'grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8'
              : `flex flex-col gap-6 ${theme.splitBorder}`
          }
        >
          {showContactFields && (
            <div>
              <SectionLabel theme={theme}>A bit about you</SectionLabel>
              <ContactNameEmailFields
                theme={theme}
                name={name}
                email={email}
                lockContactFields={lockContactFields}
                submitAttempted={submitAttempted}
                onNameChange={onNameChange}
                onEmailChange={onEmailChange}
              />
              <div className="mt-3">
                <input
                  type="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={theme.input}
                  placeholder="Phone, if you'd like"
                />
              </div>
            </div>
          )}

          <div>
            <SectionLabel theme={theme}>Anything else? (optional)</SectionLabel>
            <div className="space-y-3">
              <input
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                className={theme.input}
                placeholder={`${organizationPlaceholder} (optional)`}
              />
              {useTeamSize && teamSizeOptions ? (
                <div>
                  <p className={`text-[13px] mb-2 leading-snug ${theme.mutedText}`}>
                    {teamSizeSectionLabel} (optional)
                  </p>
                  <div
                    className="flex flex-wrap gap-2"
                    role="radiogroup"
                    aria-label={teamSizeSectionLabel}
                  >
                    {teamSizeOptions.map((opt) => {
                      const isSelected = teamSize === opt.id
                      const pillCls =
                        variant === 'light'
                          ? [
                              'px-4 h-[40px] rounded-full text-[13px] font-medium',
                              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)]',
                              'motion-safe:transition-colors motion-safe:duration-150',
                              isSelected
                                ? 'bg-[var(--signup-accent-subtle)] border border-[var(--signup-accent)] text-[var(--signup-accent)]'
                                : 'bg-[var(--signup-bg-elevated)] border border-[var(--signup-border-default)] text-[var(--signup-text-primary)] hover:bg-[var(--signup-bg-subtle)]',
                            ].join(' ')
                          : [
                              'px-4 h-[40px] rounded-full text-[13px] font-medium',
                              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#71cdd9]',
                              isSelected
                                ? theme.chipActive
                                : theme.chipDefault,
                            ].join(' ')
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          onClick={() => {
                            onTeamSizeChange(isSelected ? '' : opt.id)
                          }}
                          className={pillCls}
                        >
                          {opt.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className={`${theme.input} resize-none`}
                  placeholder={`${messagePlaceholder} (optional)`}
                />
              )}
            </div>
          </div>

          <div
            className={
              stickyMobileConfirm
                ? 'hidden lg:block md:col-span-2'
                : isStacked
                  ? 'md:col-span-2'
                  : undefined
            }
          >
            {renderConfirmHints()}
          </div>

          <div
            className={[
              stickyMobileConfirm ? 'hidden lg:block' : '',
              isStacked ? 'md:col-span-2' : 'mt-auto',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {renderConfirmButton()}
          </div>
        </div>
  )

  const stickyBar = stickyMobileConfirm ? (
    <div
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-black/92 backdrop-blur-xl px-4 pt-3"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto w-full max-w-lg space-y-2">
        {renderConfirmHints()}
        {renderConfirmButton()}
      </div>
    </div>
  ) : null

  if (isCompact) {
    return (
      <div className={shellPad}>
        {schedulingBlock}
        {stickyBar}
      </div>
    )
  }

  if (isStacked) {
    return (
      <div className={shellPad}>
        <div className="space-y-7">
          {schedulingBlock}
          {detailsColumn}
        </div>
        {stickyBar}
      </div>
    )
  }

  return (
    <div className={shellPad}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {schedulingBlock}
        {detailsColumn}
      </div>
      {stickyBar}
    </div>
  )
}

export type { CalendlyAudience }
